import React, { useEffect, useState, useRef } from 'react';

const FULL = '<MIID/>';
const PAUSE_FULL = 2400;
const PAUSE_EMPTY = 500;
const TYPE_SPEED = 180;
const ERASE_SPEED = 110;

const CodeTag: React.FC = () => {
  const [text, setText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const phaseRef = useRef<'typing' | 'pausing' | 'erasing' | 'waiting'>('typing');
  const idxRef = useRef(0);

  useEffect(() => {
    const id = setInterval(() => setShowCursor((v) => !v), 520);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    const step = () => {
      if (phaseRef.current === 'typing') {
        if (idxRef.current < FULL.length) {
          idxRef.current++;
          setText(FULL.slice(0, idxRef.current));
          timeout = setTimeout(step, TYPE_SPEED);
        } else {
          phaseRef.current = 'pausing';
          timeout = setTimeout(step, PAUSE_FULL);
        }
      } else if (phaseRef.current === 'pausing') {
        phaseRef.current = 'erasing';
        step();
      } else if (phaseRef.current === 'erasing') {
        if (idxRef.current > 0) {
          idxRef.current--;
          setText(FULL.slice(0, idxRef.current));
          timeout = setTimeout(step, ERASE_SPEED);
        } else {
          phaseRef.current = 'waiting';
          timeout = setTimeout(step, PAUSE_EMPTY);
        }
      } else {
        phaseRef.current = 'typing';
        step();
      }
    };
    timeout = setTimeout(step, 800);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <div
      aria-hidden="true"
      style={{
        fontFamily: "'Cormorant Garamond', Georgia, serif",
        fontWeight: 600,
        lineHeight: 1,
        letterSpacing: '-.02em',
        userSelect: 'none',
      }}
    >
      {text.split('').map((ch, i) => (
        <span
          key={i}
          style={{
            color:
              ch === '<' || ch === '>' ? 'var(--blue)'
              : ch === '/' ? 'var(--gold)'
              : 'var(--ink)',
          }}
        >
          {ch}
        </span>
      ))}
      <span
        style={{
          display: 'inline-block',
          width: '3px',
          height: '0.82em',
          background: 'var(--gold)',
          marginLeft: '3px',
          verticalAlign: 'middle',
          opacity: showCursor ? 1 : 0,
          transition: 'opacity .1s',
          borderRadius: '1px',
        }}
      />
    </div>
  );
};

const EnhancedHero: React.FC = () => {
  return (
    <section
      id="home"
      className="hero-section"
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #FFFFFF 0%, #F2F8FD 50%, #E8F2FA 100%)',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        paddingTop: '68px',
      }}
    >
      <style>{`
        .hero-layout {
          max-width: 1200px;
          width: 100%;
          margin: 0 auto;
          padding: 4rem 2rem;
          display: grid;
          grid-template-columns: 1fr auto;
          align-items: center;
          gap: 3rem;
          position: relative;
          z-index: 1;
        }
        .hero-code-block {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.2rem;
          padding-right: 1rem;
        }
        .hero-code-size {
          font-size: clamp(2.8rem, 6.5vw, 6rem);
          filter: drop-shadow(0 8px 40px rgba(26,111,168,.14));
        }
        @media (max-width: 860px) {
          .hero-layout {
            grid-template-columns: 1fr;
          }
          .hero-code-block {
            display: none;
          }
        }

        /* Expertises — aligné sur sec-tag (minimal, sobre) */
        .hero-expertises {
          display: flex;
          flex-direction: column;
          gap: .7rem;
        }
        .hero-expertises-label {
          display: inline-flex;
          align-items: center;
          gap: .5rem;
          font-family: 'Montserrat', sans-serif;
          font-size: .58rem;
          font-weight: 600;
          letter-spacing: .22em;
          text-transform: uppercase;
          color: var(--ink2);
        }
        .hero-expertises-bar {
          display: inline-block;
          width: 20px;
          height: 2px;
          background: var(--gold);
        }
        .hero-expertises-list {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: .9rem;
        }
        .hero-expertise-item {
          display: inline-flex;
          align-items: center;
          gap: .5rem;
          font-family: 'Montserrat', sans-serif;
          font-size: .7rem;
          font-weight: 500;
          letter-spacing: .02em;
          color: var(--ink);
          text-decoration: none;
          transition: color .2s ease;
        }
        .hero-expertise-item:hover {
          color: var(--blue);
        }
        .hero-expertise-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          flex-shrink: 0;
        }
        .hero-expertise-sep {
          color: var(--ink2);
          opacity: .3;
          font-weight: 300;
        }
        @media (max-width: 600px) {
          .hero-expertises-list { gap: .6rem; }
          .hero-expertise-item { font-size: .65rem; }
          .hero-expertise-sep { display: none; }
        }
      `}</style>

      {/* Subtle background grid */}
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(26,111,168,.025) 1px, transparent 1px), linear-gradient(90deg, rgba(26,111,168,.025) 1px, transparent 1px)', backgroundSize: '60px 60px', pointerEvents: 'none' }} />

      {/* Diagonal gradient lines */}
      <svg
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
        preserveAspectRatio="none"
        viewBox="0 0 1200 800"
      >
        <defs>
          <linearGradient id="heroLineGrad1" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="var(--blue)" stopOpacity="0" />
            <stop offset="50%" stopColor="var(--blue)" stopOpacity=".18" />
            <stop offset="100%" stopColor="var(--gold)" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="heroLineGrad2" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="var(--gold)" stopOpacity="0" />
            <stop offset="50%" stopColor="var(--gold)" stopOpacity=".15" />
            <stop offset="100%" stopColor="var(--blue)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <line x1="800" y1="-100" x2="1400" y2="900" stroke="url(#heroLineGrad1)" strokeWidth="1.5" />
        <line x1="900" y1="-50" x2="1500" y2="950" stroke="url(#heroLineGrad2)" strokeWidth="1" />
        <line x1="-100" y1="500" x2="400" y2="1000" stroke="url(#heroLineGrad1)" strokeWidth="1" opacity=".6" />
      </svg>

      <div className="hero-layout">
        {/* Left: text */}
        <div>
          <div className="hero-tag sec-tag" style={{ marginBottom: '1.8rem' }}>
            Innovation Numérique · Mauritanie
          </div>

          <h1
            className="hero-title"
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: 'clamp(2.4rem, 5vw, 4.2rem)',
              fontWeight: 600,
              color: 'var(--ink)',
              lineHeight: 1.12,
              marginBottom: '1.4rem',
            }}
          >
            Société Mauritanienne<br />
            d'<em style={{ color: 'var(--blue)', fontStyle: 'italic' }}>Innovation</em> et{' '}
            <em style={{ color: 'var(--blue)', fontStyle: 'italic' }}>Développement</em>
          </h1>

          <div className="hero-subtitle" style={{ marginBottom: '1.4rem' }}>
            <div className="gold-bar" style={{ margin: 0 }} />
          </div>

          <p
            className="hero-subtitle"
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: 'clamp(.88rem, 1.6vw, 1rem)',
              fontWeight: 300,
              color: 'var(--ink2)',
              lineHeight: 1.8,
              maxWidth: '500px',
              marginBottom: '2.5rem',
            }}
          >
            Votre partenaire technologique pour la transformation digitale en Mauritanie et en Afrique.
          </p>

          <div className="hero-cta" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '3rem' }}>
            <a href="#services" className="btn-primary">
              Découvrir MIID
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 7h12M7 1l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
            <a href="#contact" className="btn-outline">Nous contacter</a>
          </div>

          {/* Expertises — style aligné sur sec-tag (minimal, uppercase, sobre) */}
          <div className="hero-badges hero-expertises">
            <div className="hero-expertises-label">
              <span className="hero-expertises-bar" />
              Nos expertises
            </div>
            <div className="hero-expertises-list">
              {[
                { label: 'Dashboards temps réel', color: 'var(--blue)' },
                { label: 'Intégration CRM', color: 'var(--gold-dark)' },
                { label: 'Automatisation', color: '#2E7D32' },
                { label: 'IA & LLM', color: '#5E35B1' },
              ].map((p, i, arr) => (
                <React.Fragment key={p.label}>
                  <a href="#services" className="hero-expertise-item">
                    <span className="hero-expertise-dot" style={{ background: p.color }} />
                    {p.label}
                  </a>
                  {i < arr.length - 1 && <span className="hero-expertise-sep">·</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* Right: </> typewriter */}
        <div className="hero-code-block">
          <div className="hero-code-size">
            <CodeTag />
          </div>
          <span style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '.5rem', fontWeight: 600, letterSpacing: '.28em', textTransform: 'uppercase', color: 'var(--ink2)', opacity: .3 }}>
            code · innovation · impact
          </span>
        </div>
      </div>

    </section>
  );
};

export default EnhancedHero;
